/**
 * ADMIN USERS REACT QUERY HOOKS
 *
 * React Query hooks for managing users in super admin dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminCompanyService, type UserFilters } from '@/services/adminCompanyService';
import { useToast } from '@/hooks/use-toast';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Get all users with optional filtering
 */
export function useAdminUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: ['admin-users', filters],
    queryFn: () => AdminCompanyService.getAllUsers(filters),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get user stats
 */
export function useAdminUserStats() {
  return useQuery({
    queryKey: ['admin-user-stats'],
    queryFn: () => AdminCompanyService.getUserStats(),
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Update user (role, active status)
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { role?: string; is_active?: boolean } }) =>
      AdminCompanyService.updateUser(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-stats'] });
      toast({
        title: 'User Updated',
        description: 'User has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Updating User',
        description: error.message,
      });
    },
  });
}
